import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { SPACING } from '@common/theme/spacing'
import type { TimepointSubmissionUploader } from '@common/types/uploader'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: 2,
  alignSelf: 'stretch',
  bgcolor: 'background.default',
  boxShadow: 8,
  width: 640,
  maxWidth: '95vw',
}

export default function UploaderDetailsModal({
  open,
  onClose,
  data,
  isLoading = false,
  isError = false,
}: {
  open: boolean
  onClose: () => void
  data?: TimepointSubmissionUploader
  isLoading?: boolean
  isError?: boolean
}) {
  // Parse the UTC timestamp from backend and convert to the user's local timezone for display.
  // Use explicit UTC parsing so 'Z' timestamps are interpreted correctly.
  const formattedTimestamp = data?.uploaded_at
    ? dayjs.utc(data.uploaded_at).local().format('DD/MM/YYYY, hh:mm:ss A')
    : null

  return (
    <Modal
      open={open}
      onClose={onClose}
      data-testid="uploader-details-modal"
      aria-labelledby="uploader-details-modal-title"
      aria-describedby="uploader-details-modal-description"
    >
      <Paper sx={modalStyle}>
        <Stack padding={SPACING.spacingXl} spacing={SPACING.spacingSm} width="100%">
          <Typography variant="h6" color="text.primary" id="uploader-details-modal-title">
            Uploader Details
          </Typography>
        </Stack>

        <Box paddingX={SPACING.spacingXl} paddingBottom={SPACING.spacingLg} width="100%">
          {isLoading ? (
            <Stack direction="row" justifyContent="center" alignItems="center" py={SPACING.spacingXl} width="100%">
              <CircularProgress size={28} />
            </Stack>
          ) : isError ? (
            <Typography variant="body1" color="error" py={SPACING.spacingLg}>
              Unable to load uploader details.
            </Typography>
          ) : (
            <TableContainer
              sx={{
                borderRadius: SPACING.borderRadiusXl,
              }}
            >
              <Table size="small" aria-label="uploader-details-table">
                <TableHead
                  sx={{
                    bgcolor: 'paper.main',
                  }}
                >
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Uploaded at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{data?.name ?? '—'}</TableCell>
                    <TableCell>{data?.email ?? '—'}</TableCell>
                    <TableCell>{formattedTimestamp ?? '—'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Stack padding={SPACING.spacingXl} alignSelf="stretch" alignItems="flex-end">
          <Button
            variant="contained"
            sx={{
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
            }}
            onClick={onClose}
            data-testid="close-uploader-details-button"
          >
            Close
          </Button>
        </Stack>
      </Paper>
    </Modal>
  )
}
